import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createExpiringSessionStorage, ONE_DAY_MS } from './expiring-storage';

export interface SelectionItem {
  roomSlug: string;
  roomName: string;
  roomImageUrl: string | null;
  roomTypeName: string | null;
}

interface BookingStore {
  // Shared dates for the entire selection
  checkIn: string | null;   // "YYYY-MM-DD"
  checkOut: string | null;  // "YYYY-MM-DD"
  // Selected rooms (no per-item dates — all share checkIn/checkOut)
  items: SelectionItem[];
  // True when the user pressed "Book Now" for a single room (not "Add Room").
  // Prevents the multi-room SelectionBar from appearing during navigation
  // and allows cleanup on back-navigation from checkout.
  singleRoomCheckout: boolean;

  setDates: (checkIn: string, checkOut: string) => void;
  addRoom: (room: SelectionItem) => void;
  removeRoom: (roomSlug: string) => void;
  toggleRoom: (room: SelectionItem) => void;
  replaceRoom: (oldSlug: string, newRoom: SelectionItem) => void;
  setSingleRoomCheckout: (value: boolean) => void;
  /** Clear room items and single-room flag, but preserve dates. */
  clearSelection: () => void;
  clearAll: () => void;
}

export const useBookingStore = create<BookingStore>()(
  persist(
    (set) => ({
      checkIn: null,
      checkOut: null,
      items: [],
      singleRoomCheckout: false,

      setDates: (checkIn, checkOut) =>
        set({ checkIn, checkOut }),

      addRoom: (room) =>
        set((state) => {
          if (state.items.some((i) => i.roomSlug === room.roomSlug)) return state;
          return { items: [...state.items, room] };
        }),

      removeRoom: (roomSlug) =>
        set((state) => ({
          items: state.items.filter((i) => i.roomSlug !== roomSlug),
        })),

      toggleRoom: (room) =>
        set((state) => {
          const exists = state.items.some((i) => i.roomSlug === room.roomSlug);
          return {
            items: exists
              ? state.items.filter((i) => i.roomSlug !== room.roomSlug)
              : [...state.items, room],
          };
        }),

      replaceRoom: (oldSlug, newRoom) =>
        set((state) => ({
          items: state.items.map((i) =>
            i.roomSlug === oldSlug ? newRoom : i
          ),
        })),

      setSingleRoomCheckout: (value) => set({ singleRoomCheckout: value }),

      clearSelection: () => set({ items: [], singleRoomCheckout: false }),

      clearAll: () => set({ checkIn: null, checkOut: null, items: [], singleRoomCheckout: false }),
    }),
    {
      name: 'booking-selection',
      storage: createJSONStorage(() => createExpiringSessionStorage(ONE_DAY_MS)),
    }
  )
);
